import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Phone, ArrowRight, ChatCircleText, CheckCircle, Fire, Bicycle, Leaf } from '@phosphor-icons/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useCustomer } from './CustomerApp';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api/customer/auth';

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent);

export default function CustomerLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginCustomer } = useCustomer();

  // Pre-fill the phone number when arriving from an external link that
  // already collected it (e.g. the marketing site's "Sign up" popup), so
  // the customer isn't asked to type the same number twice. Only accepts
  // a plausible 10-digit Indian mobile number — anything else is ignored
  // and the field is left blank as before.
  const prefillPhone = () => {
    const raw = searchParams.get('phone') || '';
    const digits = raw.replace(/\D/g, '').slice(0, 10);
    return /^[6-9]\d{9}$/.test(digits) ? digits : '';
  };

  // Restores an in-progress verification (phone + step + code info) across
  // a page refresh — the URL itself is intentionally scrubbed of the phone
  // number for privacy right after first load, so it can't be the source
  // of truth for a later refresh. sessionStorage fills that gap: private
  // to this one tab, never visible in any URL or shared link, and cleared
  // automatically once the tab closes or login actually completes.
  const SESSION_KEY = 'sspice_login_session';
  const loadSavedSession = () => {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };
  const savedSession = loadSavedSession();

  const [phone, setPhone] = useState(() => prefillPhone() || savedSession?.phone || '');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(() => savedSession?.step || 'enter'); // 'enter' | 'verify'
  const [verifyInfo, setVerifyInfo] = useState(() => savedSession?.verifyInfo || null); // { code, receiving_number }
  const [polling, setPolling] = useState(false);
  const pollRef = useRef(null);
  const rcsHintRef = useRef(null);
  const autoContinueRef = useRef(null);
  const [showRcsHint, setShowRcsHint] = useState(false);
  // Guards against completeLogin() firing more than once — the 3s polling
  // timer AND the visibility/focus listener can both detect "verified"
  // within the same moment (confirmed in production logs: phone-login was
  // called 3 times within one second from a single successful verification).
  // clearInterval alone doesn't prevent this, since the second trigger is
  // an already-in-flight check, not a timer that can be cancelled.
  const completingRef = useRef(false);

  // Keep the saved session in sync with the real, current state — so a
  // refresh at any point during verification restores exactly where the
  // customer left off, not just the phone number.
  useEffect(() => {
    if (step === 'verify' && phone && verifyInfo) {
      try {
        sessionStorage.setItem(SESSION_KEY, JSON.stringify({ phone, step, verifyInfo }));
      } catch {
        /* sessionStorage unavailable — refresh just won't restore, no crash */
      }
    }
  }, [phone, step, verifyInfo]);

  // The phone number is captured into state above on first render. Once
  // that's done we scrub it from the visible address bar — the customer
  // never needs to see their own number sitting in the URL, and it keeps
  // it out of browser history / any screen-share or shoulder-surf moment.
  // No page reload, no navigation — just cleans up what's displayed.
  useEffect(() => {
    if (searchParams.get('phone')) {
      window.history.replaceState(null, '', window.location.pathname);
    }
    // eslint-disable-next-line
  }, []);

  // If we restored an in-progress verification from a refresh, resume
  // polling right away instead of leaving the screen stuck with no active
  // check running.
  useEffect(() => {
    if (savedSession && step === 'verify' && verifyInfo) {
      startPolling();
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); if (autoContinueRef.current) clearTimeout(autoContinueRef.current); };
  }, []);

  const handleRequestVerification = async () => {
    if (phone.length < 10) {
      toast.error('Enter a valid 10-digit phone number');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/phone/request-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Could not start verification');
      }
      const data = await res.json();
      setVerifyInfo(data);
      completingRef.current = false;  // fresh verification request — clear any prior guard
      setStep('verify');
      startPolling();
    } catch (err) {
      toast.error(err.message || 'Failed to start verification');
    } finally {
      setLoading(false);
    }
  };

  const checkVerificationOnce = async () => {
    try {
      const res = await fetch(`${API_BASE}/phone/verification-status?phone=${phone}`);
      const data = await res.json();
      if (data.verified) {
        if (completingRef.current) return true;  // already handling this — ignore duplicate trigger
        completingRef.current = true;
        if (pollRef.current) clearInterval(pollRef.current);
        if (rcsHintRef.current) clearTimeout(rcsHintRef.current);
        if (autoContinueRef.current) clearTimeout(autoContinueRef.current);
        setPolling(false);
        await completeLogin();
        return true;
      } else if (data.expired) {
        if (pollRef.current) clearInterval(pollRef.current);
        if (rcsHintRef.current) clearTimeout(rcsHintRef.current);
        if (autoContinueRef.current) clearTimeout(autoContinueRef.current);
        try { sessionStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
        setPolling(false);
        toast.error('Verification code expired — please try again');
        setStep('enter');
        return true;
      }
    } catch {
      // transient network hiccup — keep polling
    }
    return false;
  };

  const startPolling = () => {
    setPolling(true);
    setShowRcsHint(false);
    if (rcsHintRef.current) clearTimeout(rcsHintRef.current);
    if (autoContinueRef.current) clearTimeout(autoContinueRef.current);
    // If nothing's come through after a while, most likely cause is the
    // phone trying RCS instead of a real SMS — nudge them toward the fix
    // rather than leaving them stuck watching a spinner with no context.
    rcsHintRef.current = setTimeout(() => setShowRcsHint(true), 22000);
    // Verification is best-effort, not a hard gate — the backend already
    // allows an unverified login, but nothing ever asked it to unless
    // polling saw "verified" first. This is that ask: if the SMS
    // round-trip hasn't completed within 35s (forwarder offline, delayed
    // delivery, whatever), log the customer in anyway rather than leaving
    // them stuck for the full 10-minute expiry window.
    autoContinueRef.current = setTimeout(async () => {
      if (completingRef.current) return;
      completingRef.current = true;
      if (pollRef.current) clearInterval(pollRef.current);
      if (rcsHintRef.current) clearTimeout(rcsHintRef.current);
      setPolling(false);
      await completeLogin();
    }, 35000);
    pollRef.current = setInterval(checkVerificationOnce, 3000);
  };

  // Mobile browsers routinely pause a backgrounded tab's timers (including
  // our setInterval) while the customer is in their Messages app sending the
  // SMS. Without this, the screen can sit stuck on "Waiting…" until the
  // interval happens to resume — feels like the app "lost" the flow. This
  // catches the exact moment they switch back and checks immediately instead
  // of waiting for the throttled timer.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && step === 'verify') {
        checkVerificationOnce();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
    // eslint-disable-next-line
  }, [step, phone]);

  const completeLogin = async () => {
    try {
      const res = await fetch(`${API_BASE}/phone-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Login failed');
      }
      const { token, customer } = await res.json();
      loginCustomer(token, customer);
      try { sessionStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
      setStep('splash');
      // Brief branded success screen, then continue on to the app —
      // gives the customer clear, noticeable confirmation that login
      // actually worked, instead of jumping straight to the order page.
      setTimeout(() => {
        navigate(customer.needs_name ? '/order/setup-profile' : '/order');
      }, 1400);
    } catch (err) {
      toast.error(err.message || 'Login failed');
      setStep('enter');
    }
  };

  const openSmsApp = () => {
    if (!verifyInfo) return;
    const body = `SSPICE ${verifyInfo.code}`;
    const separator = isIOS() ? '&' : '?';
    const link = `sms:${verifyInfo.receiving_number}${separator}body=${encodeURIComponent(body)}`;
    window.location.href = link;
  };

  if (step === 'splash') {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center bg-sspice"
        data-testid="login-splash-screen"
      >
        <motion.div
          initial={{ scale: 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 260, damping: 20 }}
          className="flex flex-col items-center text-center px-6"
        >
          <div className="w-24 h-24 rounded-3xl bg-white flex items-center justify-center mb-6 shadow-2xl">
            <img src="/icon-512x512.png" alt="Sspice Pizza" className="w-16 h-16" />
          </div>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3, type: 'spring', stiffness: 300, damping: 15 }}
            className="w-10 h-10 rounded-full bg-white flex items-center justify-center mb-3"
          >
            <CheckCircle size={26} weight="fill" className="text-sspice" />
          </motion.div>
          <h1 className="text-2xl font-extrabold text-white">Welcome!</h1>
          <p className="text-sm text-white/85 mt-1">You're logged in — let's order</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen relative flex flex-col items-center justify-end sm:justify-center overflow-hidden bg-black"
      data-testid="login-page"
    >
      {/* Background photo — dimmed so white text/card stay readable at any screen size */}
      <img
        src="/images/login-hero-pizza.jpg"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-black/40 sm:bg-gradient-to-r sm:from-black/85 sm:via-black/55 sm:to-black/25" />

      {/* Brand mark — stacks above the card on mobile, sits beside it from tablet width up */}
      <div className="relative z-10 w-full max-w-5xl mx-auto px-6 pt-12 sm:pt-0 sm:flex sm:flex-col sm:items-center sm:justify-center sm:h-screen sm:text-center">
        <div className="sm:max-w-md sm:mx-auto">
          <div className="inline-block bg-white rounded-2xl px-6 py-4 mb-4 shadow-lg">
            <img
              src="/logo-full.png"
              alt="Sspice Pizza"
              className="h-10 sm:h-12 w-auto"
            />
          </div>
          <p className="text-sm sm:text-base text-white/80 mt-2 leading-snug">
            Fresh ingredients. <span className="text-sspice font-semibold">Bold flavours.</span><br className="hidden sm:block" /> Delivered to your doorstep.
          </p>

          {/* Card sits inline here on larger screens; on mobile it renders separately below */}
          <div className="hidden sm:block mt-8">
            <LoginCard
              step={step} phone={phone} setPhone={setPhone} loading={loading}
              handleRequestVerification={handleRequestVerification}
              verifyInfo={verifyInfo} openSmsApp={openSmsApp} polling={polling}
              pollRef={pollRef} setPolling={setPolling} setStep={setStep}
              showRcsHint={showRcsHint} setShowRcsHint={setShowRcsHint}
              rcsHintRef={rcsHintRef} SESSION_KEY={SESSION_KEY}
            />
          </div>
        </div>
      </div>

      {/* Card renders here on mobile (stacked below the brand block) */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full px-4 pb-8 pt-6 sm:hidden"
      >
        <LoginCard
          step={step} phone={phone} setPhone={setPhone} loading={loading}
          handleRequestVerification={handleRequestVerification}
          verifyInfo={verifyInfo} openSmsApp={openSmsApp} polling={polling}
          pollRef={pollRef} setPolling={setPolling} setStep={setStep}
          showRcsHint={showRcsHint} setShowRcsHint={setShowRcsHint}
          rcsHintRef={rcsHintRef} SESSION_KEY={SESSION_KEY}
        />
      </motion.div>
    </div>
  );
}

function LoginCard({ step, phone, setPhone, loading, handleRequestVerification, openSmsApp, polling, pollRef, setPolling, setStep, showRcsHint, setShowRcsHint, rcsHintRef, SESSION_KEY }) {
  return (
    <div className="w-full max-w-sm mx-auto bg-white rounded-3xl shadow-2xl p-6" data-testid="login-card">
      {step === 'enter' && (
        <>
          <h2 className="text-xl font-bold text-gray-900 mb-1 text-center">Welcome!</h2>
          <p className="text-sm text-gray-500 mb-6 text-center">Enter your number to start ordering</p>

          <div className="relative mb-2">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center gap-2 text-gray-400">
              <span className="text-sm font-medium">+91</span>
              <span className="text-gray-200">|</span>
            </div>
            <input
              data-testid="login-phone-input"
              type="tel"
              maxLength={10}
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => e.key === 'Enter' && handleRequestVerification()}
              placeholder="Enter phone number"
              className="w-full pl-20 pr-4 py-4 bg-white border border-gray-200 rounded-xl text-gray-900 text-lg focus:outline-none focus:ring-2 focus:ring-red-100 focus:border-sspice transition-all"
            />
          </div>
          <p className="text-[11px] text-gray-400 mb-5">
            We verify this number is really yours via a one-tap text message — no OTP to type.
          </p>
          <button
            data-testid="login-continue-btn"
            onClick={handleRequestVerification}
            disabled={loading || phone.length < 10}
            className="w-full py-3.5 bg-sspice hover:bg-sspice-hover disabled:opacity-40 text-white font-semibold rounded-full flex items-center justify-center gap-2 transition-all"
          >
            {loading ? 'Please wait...' : 'Continue'}
            {!loading && <ArrowRight size={18} weight="bold" />}
          </button>

          <p className="text-[11px] text-gray-400 mt-4 text-center">
            By continuing, you agree to our Terms of Service and Privacy Policy
          </p>

          <div className="mt-5 pt-4 border-t border-gray-100 grid grid-cols-3 gap-2">
            <FeatureIcon icon={<Fire size={16} weight="fill" />} title="Freshly Made" sub="To perfection" />
            <FeatureIcon icon={<Bicycle size={16} weight="fill" />} title="Fast Delivery" sub="Hot & on time" />
            <FeatureIcon icon={<Leaf size={16} weight="fill" />} title="Quality" sub="Always best" />
          </div>
        </>
      )}

      {step === 'verify' && (
        <div className="text-center">
          <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
            <ChatCircleText size={28} weight="duotone" className="text-sspice" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-1">One tap to verify</h2>
          <p className="text-sm text-gray-500 mb-6">
            We've pre-filled a text message for you — just hit Send in your messaging app, nothing to type.
          </p>

          <button
            data-testid="open-sms-app-btn"
            onClick={openSmsApp}
            className="w-full py-3.5 bg-sspice hover:bg-sspice-hover text-white font-semibold rounded-full flex items-center justify-center gap-2 transition-all mb-4"
          >
            <ChatCircleText size={18} weight="bold" />
            Open Messages & Send
          </button>

          <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
            {polling && (
              <>
                <span className="w-2 h-2 rounded-full bg-sspice animate-pulse" />
                Waiting for your message…
              </>
            )}
          </div>

          {showRcsHint && polling && (
            <p className="text-xs text-gray-400 text-center mt-2 px-4 leading-relaxed" data-testid="rcs-hint">
              Didn't go through? Open your phone's messaging app and look for
              a "Send as SMS" option — tap that instead.
            </p>
          )}

          <button
            data-testid="back-to-phone-entry-btn"
            onClick={() => { if (pollRef.current) clearInterval(pollRef.current); if (rcsHintRef.current) clearTimeout(rcsHintRef.current); if (autoContinueRef.current) clearTimeout(autoContinueRef.current); try { sessionStorage.removeItem(SESSION_KEY); } catch { /* ignore */ } setPolling(false); setShowRcsHint(false); setStep('enter'); }}
            className="w-full mt-6 py-2 text-sm text-gray-400 hover:text-sspice transition-colors"
          >
            Wrong number? Go back
          </button>
        </div>
      )}
    </div>
  );
}

function FeatureIcon({ icon, title, sub }) {
  return (
    <div className="flex flex-col items-center text-center gap-1">
      <div className="w-8 h-8 rounded-full bg-sspice text-white flex items-center justify-center">
        {icon}
      </div>
      <div className="text-[9px] font-semibold text-gray-800 leading-tight">{title}</div>
      <div className="text-[8px] text-gray-400 leading-tight">{sub}</div>
    </div>
  );
}
